import {
  IconName,
  KBButton,
  KBFlex,
  KBIcon,
  KBImage,
  KBTypography,
} from '@/components/atoms'
import PageContentLayout from '@/components/layouts/PageContentLayout'
import { CONTACT_EMAIL_ADDRESS } from '@/utils/constants'
import type { FeatureTier } from '@/store/subscriptionFeature.atom'
import { currentTierAtom } from '@/store/pricing.atom'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
const { Text } = KBTypography
export interface KBUpgradeItemType {
  itemTile: string
  itemDescription: string
  icon: IconName
}
interface KBUpgradeProps {
  title?: ReactNode
  subtitle?: string
  description?: string
  button?: ReactNode
  items?: KBUpgradeItemType[]
  type?: 'default' | 'visitor'
  /**
   * The upgrade tier type (RFC-004)
   * - 'premium': Show "Upgrade to Premium" messaging
   * - 'enterprise': Show "Upgrade to Enterprise" messaging
   * Defaults to 'premium' for backwards compatibility
   */
  upgradeType?: FeatureTier
}

const KBUpgrade = (props: KBUpgradeProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentTier = useAtomValue(currentTierAtom)
  const {
    title,
    subtitle,
    description,
    button,
    items,
    type = 'default',
    upgradeType = 'premium',
  } = props

  // Determine button behavior based on user's current subscription tier (not feature tier)
  // ENTERPRISE users should see "Contact Us", others should see "Upgrade Now"
  const isEnterprise = currentTier === 'ENTERPRISE'

  // Determine subtitle and description based on upgradeType (RFC-004)
  const defaultSubtitle =
    upgradeType === 'enterprise'
      ? t('common.upgradeToEnterprise', 'Upgrade to Enterprise')
      : t('common.upgradeToPremium', 'Upgrade to Premium')

  const defaultDescription =
    upgradeType === 'enterprise'
      ? t(
          'common.enterpriseUpgradeDescription',
          'This feature requires an Enterprise subscription. Contact us to learn more about Enterprise features and pricing.'
        )
      : t(
          'common.premiumUpgradeDescription',
          'This feature requires a Premium subscription. Upgrade your plan to unlock this and other premium features.'
        )

  const displaySubtitle = subtitle ?? defaultSubtitle
  const displayDescription = description ?? defaultDescription

  return (
    <PageContentLayout title={title}>
      <div className="tw-m-[28px_auto_0px] tw-w-[1024px]">
        <KBFlex
          gap={0}
          className="tw-mb-5 tw-rounded-md tw-border tw-border-solid tw-border-[var(--wox-color-fill-quinary)] "
          style={{
            background:
              type === 'default'
                ? 'linear-gradient(0deg, #F9E8D9 0%, #F9E8D9 100%)'
                : 'linear-gradient(0deg, #E6EFFF 0%, #E6EFFF 100%)',
          }}
        >
          <div className="tw-box-border tw-w-[512px] tw-p-8">
            <Text className="tw-mb-3 tw-block tw-text-2xl tw-font-bold tw-text-[#1F2937]">
              {displaySubtitle}
            </Text>
            <Text className="tw-mb-8  tw-block tw-text-[#1F2937]">
              {displayDescription}
            </Text>
            <KBFlex align="center" gap={12}>
              {type === 'default' ? (
                <>
                  {isEnterprise ? (
                    <KBButton
                      icon={<KBIcon name="mail" />}
                      type="primary"
                      href={`mailto:${CONTACT_EMAIL_ADDRESS}`}
                    >
                      {t('common.contactUs')}
                    </KBButton>
                  ) : (
                    <KBButton
                      icon={<KBIcon name="sparkles" />}
                      type="primary"
                      onClick={() => navigate('/admin/space-settings/billing')}
                    >
                      {t('recommendation.upgradeNow', 'Upgrade Now')}
                    </KBButton>
                  )}
                  <KBButton
                    type="text"
                    className="tw-mr-4 tw-text-[#1F2937]  hover:tw-bg-transparent"
                  >
                    <KBFlex
                      gap={8}
                      align="center"
                      onClick={() => {
                        window.open('https://woxday.com/', '_blank')
                      }}
                    >
                      {t('common.knowMore')}
                      <KBIcon name="external-link" />
                    </KBFlex>
                  </KBButton>
                </>
              ) : (
                button
              )}
            </KBFlex>
          </div>
          <KBImage
            preview={false}
            src={
              type === 'default'
                ? '/subscribedFeature/background.svg'
                : '/subscribedFeature/visitorBackground.svg'
            }
          />
        </KBFlex>
        <KBFlex gap={20}>
          {items?.map((item, index) => {
            return (
              <div
                key={index}
                className="tw-box-border tw-w-[328px] tw-rounded-md tw-bg-[var(--wox-color-fill-quinary)] tw-p-6"
              >
                <KBIcon
                  name={item.icon}
                  className="tw-mb-4 tw-block tw-text-[var(--wox-color-text)]"
                  size={20}
                />
                <Text className="tw-mb-1 tw-block tw-text-base tw-font-medium">
                  {item.itemTile}
                </Text>
                <Text>{item.itemDescription}</Text>
              </div>
            )
          })}
        </KBFlex>

        {/* {button} */}
      </div>
    </PageContentLayout>
  )
}

export default KBUpgrade
